import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { UserContext } from '../Contexts/UserContext';
import { Legend, Tooltip, BarChart, CartesianGrid, XAxis, YAxis, Bar, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';
import { FaUser, FaCalendarAlt, FaCheckCircle, FaTimesCircle, FaCog, FaFingerprint } from 'react-icons/fa';

const RunShowPage = () => {
    let { id } = useParams();
    const { user } = useContext(UserContext);
    const [qpAdmRun, setQpAdmRun] = useState();

    const getqpAdmRun = useCallback(async () => {
        const response = await fetch(`${process.env.REACT_APP_API_URL}qpadm_runs/qpadm_runs/${id}`, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user?.token}`,
            },
        });

        if (!response.ok) {
            console.log('Network response was not ok');
        }

        return response.json()
    }, [id, user?.token])

    useEffect(() => {
        // Fetch user data, static runs, and rotation runs from backend here
        // Example: setUser(data);
        // if (user?.token) {
        getqpAdmRun().then((res) => {
            setQpAdmRun(res)
        })
        // }
    }, [user, getqpAdmRun]);

    const styles = {
        pageContainer: {
            backgroundColor: '#f8f9fa',
            minHeight: '100vh',
            padding: '2rem 0',
        },
        header: {
            background: 'linear-gradient(135deg, #1a365d 0%, #2C5282 100%)',
            borderRadius: '16px',
            padding: '2rem',
            color: 'white',
            marginBottom: '2rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
        headerTitle: {
            fontSize: '1.75rem',
            fontWeight: '700',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
        },
        headerSubtitle: {
            fontSize: '1.1rem',
            opacity: '0.9',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
        },
        card: {
            border: 'none',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            height: '100%',
            background: '#ffffff',
        },
        cardHeader: {
            background: 'linear-gradient(135deg, #2C5282 0%, #1a365d 100%)',
            color: 'white',
            padding: '1.5rem',
            borderBottom: 'none',
        },
        cardTitle: {
            fontSize: '1.25rem',
            fontWeight: '600',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
        },
        cardBody: {
            padding: '1.5rem',
        },
        populationSection: {
            background: '#ffffff',
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        },
        populationTitle: {
            fontSize: '1.25rem',
            fontWeight: '600',
            marginBottom: '1.5rem',
            color: '#2d3748',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
        },
        populationItem: {
            background: '#f8fafc',
            borderRadius: '8px',
            padding: '1rem 1.5rem',
            marginBottom: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            },
        },
        chartContainer: {
            background: '#ffffff',
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            marginBottom: '2rem',
        },
        chartTitle: {
            fontSize: '1.5rem',
            fontWeight: '600',
            marginBottom: '1.5rem',
            color: '#2d3748',
            textAlign: 'center',
        },
        outputContainer: {
            background: '#ffffff',
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        },
        outputTitle: {
            fontSize: '1.5rem',
            fontWeight: '600',
            marginBottom: '1.5rem',
            color: '#2d3748',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
        },
        outputContent: {
            background: '#f8fafc',
            padding: '1.5rem',
            borderRadius: '8px',
            maxHeight: '500px',
            overflowY: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            lineHeight: '1.6',
            color: '#4a5568',
            textAlign: 'left',
        },
        badge: {
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            fontWeight: '500',
            fontSize: '0.9rem',
        },
        icon: {
            fontSize: '1.2rem',
        },
    };

    const extractDataFromString = (inputText) => {
        // Regular Expressions for matching each section's data
        const leftPopsRegex = /left pops:\s*([\s\S]*?)(?=\s*right pops:|$)/;
        const bestCoefficientsRegex = /best coefficients:\s*([\s\S]*?)(?=\s*totmean:|$)/;
        const totmeanRegex = /totmean:\s*([\s\S]*?)(?=\s*boot mean:|$)/;
        const bootMeanRegex = /boot mean:\s*([\s\S]*?)(?=\s*std. errors:|$)/;
        const stdErrorsRegex = /std. errors:\s*([\s\S]*?)(?=\s*error covariance|$)/;

        // Extract sections using the regex patterns
        const leftPopsMatch = inputText?.match(leftPopsRegex);
        const bestCoefficientsMatch = inputText?.match(bestCoefficientsRegex);
        const totmeanMatch = inputText?.match(totmeanRegex);
        const bootMeanMatch = inputText?.match(bootMeanRegex);
        const stdErrorsMatch = inputText?.match(stdErrorsRegex);

        // Prepare the extracted data
        const extractedData = {
            leftPops: leftPopsMatch ? leftPopsMatch[1].trim().split('\n') : [],
            bestCoefficients: bestCoefficientsMatch ? bestCoefficientsMatch[1].trim().split(' ').filter(n => n) : [],
            totmean: totmeanMatch ? totmeanMatch[1].trim().split(' ').filter(n => n) : [],
            bootMean: bootMeanMatch ? bootMeanMatch[1].trim().split(' ').filter(n => n) : [],
            stdErrors: stdErrorsMatch ? stdErrorsMatch[1].trim().split(' ').filter(n => n) : [],
        };

        const title = extractedData?.leftPops.shift()

        const data = extractedData.leftPops?.map((pop, index) => {
            // If bestCoefficients does not have a value for this index, return a default value (e.g., 0 or NaN)
            const value = extractedData.bestCoefficients[index] ? Math.round(parseFloat(extractedData.bestCoefficients[index]) * 100, 4) : 0;
            const error = extractedData.stdErrors[index] ? parseFloat(extractedData.stdErrors[index]) * 100 : 0;

            return {
                name: pop,
                value: value,
                error: error,
            };
        });

        return { data: [...data], title };
    };

    console.log(extractDataFromString(qpAdmRun?.file));

    return (
        <div style={styles.pageContainer}>
            <Container>
                {/* Header Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <div style={styles.header}>
                        <div style={styles.headerTitle}>
                            <FaFingerprint style={styles.icon} />
                            Run Details: {qpAdmRun?.uuid?.toUpperCase()}
                        </div>
                        <div style={styles.headerSubtitle}>
                            <FaCalendarAlt style={styles.icon} />
                            {new Date(qpAdmRun?.created_at?.split('T')[0]).toDateString()}
                        </div>
                    </div>
                </motion.div>

                {/* Run Info Cards */}
                <Row className="g-4 mb-4">
                    <Col md={4}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <Card style={styles.card}>
                                <Card.Body style={styles.cardBody}>
                                    <div style={styles.cardTitle}>
                                        <FaCog style={styles.icon} />
                                        Run Type
                                    </div>
                                    <Badge
                                        bg="primary"
                                        style={styles.badge}
                                        className="mt-3"
                                    >
                                        {qpAdmRun?.run_type?.toUpperCase()}
                                    </Badge>
                                </Card.Body>
                            </Card>
                        </motion.div>
                    </Col>

                    <Col md={4}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            <Card style={styles.card}>
                                <Card.Body style={styles.cardBody}>
                                    <div style={styles.cardTitle}>
                                        {qpAdmRun?.success ? (
                                            <FaCheckCircle style={{ ...styles.icon, color: '#48bb78' }} />
                                        ) : (
                                            <FaTimesCircle style={{ ...styles.icon, color: '#f56565' }} />
                                        )}
                                        Status
                                    </div>
                                    <Badge
                                        bg={qpAdmRun?.success ? "success" : "danger"}
                                        style={styles.badge}
                                        className="mt-3"
                                    >
                                        {qpAdmRun?.success ? 'SUCCESS' : 'FAILED'}
                                    </Badge>
                                </Card.Body>
                            </Card>
                        </motion.div>
                    </Col>

                    <Col md={4}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                        >
                            <Card style={styles.card}>
                                <Card.Body style={styles.cardBody}>
                                    <div style={styles.cardTitle}>
                                        <FaUser style={styles.icon} />
                                        User
                                    </div>
                                    <Badge
                                        bg="info"
                                        style={styles.badge}
                                        className="mt-3"
                                    >
                                        {qpAdmRun?.user?.username}
                                    </Badge>
                                </Card.Body>
                            </Card>
                        </motion.div>
                    </Col>
                </Row>

                {/* Populations Section */}
                <Row className="g-4 mb-4">
                    <Col md={6}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.5 }}
                        >
                            <div style={styles.populationSection}>
                                <h3 style={styles.populationTitle}>
                                    Left Populations
                                </h3>
                                {qpAdmRun?.left_pops?.map((sample, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.3 }}
                                        style={styles.populationItem}
                                    >
                                        {sample}
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </Col>

                    <Col md={6}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.6 }}
                        >
                            <div style={styles.populationSection}>
                                <h3 style={styles.populationTitle}>
                                    Right Populations
                                </h3>
                                {qpAdmRun?.right_pops?.map((sample, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.3 }}
                                        style={styles.populationItem}
                                    >
                                        {sample}
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </Col>
                </Row>

                {/* Chart Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.7 }}
                >
                    <div style={styles.chartContainer}>
                        <h3 style={styles.chartTitle}>
                            {extractDataFromString(qpAdmRun?.file).title}
                        </h3>
                        <ResponsiveContainer width="100%" height={400}>
                            <BarChart
                                data={extractDataFromString(qpAdmRun?.file).data}
                                margin={{
                                    top: 20,
                                    right: 30,
                                    left: 20,
                                    bottom: 5,
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="name"
                                    tick={{ fill: '#4a5568' }}
                                    tickLine={{ stroke: '#4a5568' }}
                                />
                                <YAxis
                                    tick={{ fill: '#4a5568' }}
                                    tickLine={{ stroke: '#4a5568' }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#ffffff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    }}
                                />
                                <Legend
                                    wrapperStyle={{
                                        paddingTop: '20px',
                                    }}
                                />
                                <Bar
                                    dataKey="value"
                                    fill="#4299E1"
                                    radius={[4, 4, 0, 0]}
                                    name="Value"
                                />
                                <Bar
                                    dataKey="error"
                                    fill="#48BB78"
                                    radius={[4, 4, 0, 0]}
                                    name="Error"
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </motion.div>

                {/* Output Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                >
                    <div style={styles.outputContainer}>
                        <h3 style={styles.outputTitle}>
                            <FaCog style={styles.icon} />
                            Static Output
                        </h3>
                        <div
                            style={styles.outputContent}
                            dangerouslySetInnerHTML={{
                                __html: qpAdmRun?.file
                                    ?.replaceAll('\n', ' <br /> ')
                                    ?.replaceAll(' ', '     '),
                            }}
                        />
                    </div>
                </motion.div>
            </Container>
        </div>
    );
};

export default RunShowPage;
